import { useCallback, useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";

import { supabase } from "../../../services";

import { useSignOut } from "../useSignOut";

import type { Session } from "@supabase/gotrue-js/src/lib/types";

// 2 hours
const IDLE_TIMEOUT = 1000 * 60 * 60 * 2;

const LAST_ACTIVITY = "lastActivity";

export const useSignOutOnIdle = () => {
  const [isSignedIn, setSignIn] = useState(false);
  const [sessionCheckComplete, setSessionCheckComplete] = useState(false);

  const signOutCallback = useSignOut();

  const clearLastActivity = useCallback(() => {
    localStorage.removeItem(LAST_ACTIVITY);
  }, []);

  const signOut = useCallback(() => {
    clearLastActivity();
    signOutCallback();
  }, [clearLastActivity, signOutCallback]);

  const updateLastActivity = useCallback(() => {
    localStorage.setItem(LAST_ACTIVITY, Date.now().toString());
  }, []);

  const checkIfSignedIn = useCallback(
    (session: Session | null) => {
      const user = session?.user;
      const lastActivity = localStorage.getItem(LAST_ACTIVITY);

      // If the user is signed in and there is a last activity time
      if (user && lastActivity) {
        const remainingTime = IDLE_TIMEOUT - (Date.now() - parseInt(lastActivity));

        // If the remaining time is less than 0, sign out
        if (remainingTime < 0) {
          signOut();

          // If the remaining time is greater than 0, update the last activity and set the user as signed in
        } else {
          updateLastActivity();
          setSignIn(true);
        }

        // If the user is signed in and there is no last activity time
      } else if (user && !lastActivity) {
        updateLastActivity();
        setSignIn(true);

        // If the user is not signed in, set the user as signed out
      } else {
        clearLastActivity();
        setSignIn(false);
      }

      // Set the session check as complete
      setSessionCheckComplete(true);
    },
    [clearLastActivity, signOut, updateLastActivity]
  );

  useEffect(() => {
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_, session) => {
      checkIfSignedIn(session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [checkIfSignedIn]);

  useIdleTimer({
    onIdle: signOut,
    onAction: updateLastActivity,
    debounce: 500,
    timeout: IDLE_TIMEOUT,
    disabled: !isSignedIn
  });

  return sessionCheckComplete;
};
